/**
 * Admin Unified Housing
 * ----------------------
 * Single pane of glass across every housing system on the platform:
 *   - School Housing (per-tenant hostels)
 *   - Standalone / Event Accommodation (hotels, guest rooms)
 *
 * Reads from /api/housing/*
 */
import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '@/lib/store';
import { DashboardLayout } from '@/components/layouts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import {
  Building2, BedDouble, Users, TrendingUp, RefreshCw, Loader2,
  Home, GraduationCap, CalendarDays, Link2, Unlink, Plus, UserCheck, LogOut,
  LogIn, LayoutGrid, Zap, MapPin
} from 'lucide-react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { getToken } from '@/lib/authToken';
import { useOpsRole } from '@/hooks/useOpsRole';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmtMoney = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
function KpiCard({ title, value, icon: Icon, tone = 'indigo', sub }) {
  const tones = {
    indigo: 'from-indigo-500/15 to-indigo-500/5 text-indigo-600',
    emerald: 'from-emerald-500/15 to-emerald-500/5 text-emerald-600',
    amber: 'from-amber-500/15 to-amber-500/5 text-amber-600',
    rose: 'from-rose-500/15 to-rose-500/5 text-rose-600',
  };
  return (
    <Card className={`border bg-gradient-to-br ${tones[tone]}`} data-testid={`housing-kpi-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-slate-500">{title}</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
            {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
          </div>
          <div className={`p-2 rounded-lg bg-white/60 ${tones[tone]}`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

const ScopeBadge = ({ scope }) =>
  scope === 'school'
    ? <Badge className="bg-indigo-100 text-indigo-700 hover:bg-indigo-100"><GraduationCap className="h-3 w-3 mr-1" /> School</Badge>
    : <Badge className="bg-amber-100 text-amber-700 hover:bg-amber-100"><CalendarDays className="h-3 w-3 mr-1" /> Event</Badge>;

export default function AdminUnifiedHousing() {
  const { token } = useAuthStore();
  const hdrs = () => ({ Authorization: `Bearer ${token || getToken()}`, 'Content-Type': 'application/json' });
  // Role-gating — only housing managers/wardens with housing.manage can
  // see Cross-scope Links + create-building actions. Backend enforces too.
  const { can, isSuper, loading: roleLoading } = useOpsRole('housing');

  const [tenantId, setTenantId] = useState('');
  const [scopeFilter, setScopeFilter] = useState('all');
  const [linkDialog, setLinkDialog] = useState(null);  // { scope, roomId, roomNumber }
  const [linkTarget, setLinkTarget] = useState('');    // counterpart room id
  const [linkNote, setLinkNote] = useState('');
  // Create-building / create-room state
  const [buildingDialog, setBuildingDialog] = useState(false);
  const [bForm, setBForm] = useState({ scope: 'event', name: '', tenant_id: '', description: '', gender: 'mixed', address: '', total_floors: 1 });
  const [roomDialog, setRoomDialog] = useState(null); // { building }
  const [rForm, setRForm] = useState({ room_number: '', capacity: 1, room_type: 'standard', floor: 1, price_per_night: 0 });
  const qc = useQueryClient();

  const scopeQS = scopeFilter === 'all' ? '' : `&scope=${scopeFilter}`;
  const tenantQS = tenantId ? `&tenant_id=${tenantId}` : '';

  const { data: overview, isLoading: loadingOv, refetch: refetchOv } = useQuery({
    queryKey: ['housing-unified-overview', tenantId],
    queryFn: async () => {
      const r = await fetch(`${API_URL}/api/housing/overview?_=1${tenantQS}`, { headers: hdrs() });
      if (!r.ok) throw new Error('Failed to load overview');
      return r.json();
    },
  });

  const { data: blocks = [], isLoading: loadingB } = useQuery({
    queryKey: ['housing-unified-blocks', tenantId, scopeFilter],
    queryFn: async () => {
      const r = await fetch(`${API_URL}/api/housing/blocks?_=1${tenantQS}${scopeQS}`, { headers: hdrs() });
      if (!r.ok) throw new Error('Failed');
      return r.json();
    },
  });

  const { data: rooms = [], isLoading: loadingR } = useQuery({
    queryKey: ['housing-unified-rooms', tenantId, scopeFilter],
    queryFn: async () => {
      const r = await fetch(`${API_URL}/api/housing/rooms?_=1${tenantQS}${scopeQS}&limit=500`, { headers: hdrs() });
      if (!r.ok) throw new Error('Failed');
      return r.json();
    },
  });

  const { data: allocs = [], isLoading: loadingA } = useQuery({
    queryKey: ['housing-unified-allocs', tenantId, scopeFilter],
    queryFn: async () => {
      const r = await fetch(`${API_URL}/api/housing/allocations?_=1${tenantQS}${scopeQS}&limit=300`, { headers: hdrs() });
      if (!r.ok) throw new Error('Failed');
      return r.json();
    },
  });

  const { data: links = [], isLoading: loadingL } = useQuery({
    queryKey: ['housing-unified-links'],
    queryFn: async () => {
      const r = await fetch(`${API_URL}/api/housing/links`, { headers: hdrs() });
      if (!r.ok) throw new Error('Failed');
      return r.json();
    },
  });

  const { data: tenants = [] } = useQuery({
    queryKey: ['school-tenants-for-housing'],
    queryFn: async () => {
      const r = await fetch(`${API_URL}/api/school/tenants`, { headers: hdrs() });
      return r.ok ? r.json() : [];
    },
    staleTime: 60000 * 5,
  });

  const createLink = useMutation({
    mutationFn: async ({ schoolRoomId, eventRoomId, note }) => {
      const r = await fetch(`${API_URL}/api/housing/link`, {
        method: 'POST',
        headers: hdrs(),
        body: JSON.stringify({ school_room_id: schoolRoomId, event_room_id: eventRoomId, note }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Link failed');
      }
      return r.json();
    },
    onSuccess: () => {
      toast.success('Rooms linked — they now share availability.');
      qc.invalidateQueries({ queryKey: ['housing-unified-links'] });
      qc.invalidateQueries({ queryKey: ['housing-unified-rooms'] });
      setLinkDialog(null); setLinkTarget(''); setLinkNote('');
    },
    onError: (e) => toast.error(e.message),
  });

  const deleteLink = useMutation({
    mutationFn: async (id) => {
      const r = await fetch(`${API_URL}/api/housing/link/${id}`, { method: 'DELETE', headers: hdrs() });
      if (!r.ok) throw new Error('Unlink failed');
      return r.json();
    },
    onSuccess: () => {
      toast.success('Rooms unlinked.');
      qc.invalidateQueries({ queryKey: ['housing-unified-links'] });
      qc.invalidateQueries({ queryKey: ['housing-unified-rooms'] });
    },
    onError: (e) => toast.error(e.message),
  });

  const invalidateAll = () => {
    ['housing-unified-overview', 'housing-unified-blocks', 'housing-unified-rooms', 'housing-unified-allocs'].forEach(k => qc.invalidateQueries({ queryKey: [k] }));
  };

  const createBuilding = useMutation({
    mutationFn: async (body) => {
      const r = await fetch(`${API_URL}/api/housing/buildings`, { method: 'POST', headers: hdrs(), body: JSON.stringify(body) });
      if (!r.ok) { const err = await r.json().catch(() => ({})); throw new Error(err.detail || 'Create failed'); }
      return r.json();
    },
    onSuccess: () => {
      toast.success('Building created');
      invalidateAll();
      setBuildingDialog(false);
      setBForm({ scope: 'event', name: '', tenant_id: '', description: '', gender: 'mixed', address: '', total_floors: 1 });
    },
    onError: (e) => toast.error(e.message),
  });

  const createRoom = useMutation({
    mutationFn: async (body) => {
      const r = await fetch(`${API_URL}/api/housing/rooms`, { method: 'POST', headers: hdrs(), body: JSON.stringify(body) });
      if (!r.ok) { const err = await r.json().catch(() => ({})); throw new Error(err.detail || 'Create failed'); }
      return r.json();
    },
    onSuccess: () => {
      toast.success('Room created');
      invalidateAll();
      setRoomDialog(null);
      setRForm({ room_number: '', capacity: 1, room_type: 'standard', floor: 1, price_per_night: 0 });
    },
    onError: (e) => toast.error(e.message),
  });

  const checkoutAlloc = useMutation({
    mutationFn: async ({ id, scope }) => {
      const r = await fetch(`${API_URL}/api/housing/allocations/${id}/checkout`, { method: 'POST', headers: hdrs(), body: JSON.stringify({ scope }) });
      if (!r.ok) { const err = await r.json().catch(() => ({})); throw new Error(err.detail || 'Checkout failed'); }
      return r.json();
    },
    onSuccess: () => {
      toast.success('Checked out');
      invalidateAll();
    },
    onError: (e) => toast.error(e.message),
  });

  const checkinAlloc = useMutation({
    mutationFn: async ({ id, scope }) => {
      const r = await fetch(`${API_URL}/api/housing/allocations/${id}/check-in`, { method: 'POST', headers: hdrs(), body: JSON.stringify({ scope }) });
      if (!r.ok) { const err = await r.json().catch(() => ({})); throw new Error(err.detail || 'Check-in failed'); }
      return r.json();
    },
    onSuccess: () => {
      toast.success('Checked in');
      invalidateAll();
    },
    onError: (e) => toast.error(e.message),
  });

  // Bulk allocate
  const [bulkDialog, setBulkDialog] = useState(false);
  const [bulkScopeId, setBulkScopeId] = useState('');
  const [bulkBuildingId, setBulkBuildingId] = useState('');
  const [bulkSelection, setBulkSelection] = useState({});

  const { data: bulkRegs = [] } = useQuery({
    queryKey: ['scope-regs-for-bulk', bulkScopeId],
    queryFn: async () => {
      if (!bulkScopeId) return [];
      const r = await fetch(`${API_URL}/api/scope-reg/by-scope?scope_type=event&scope_id=${bulkScopeId}&limit=200`, { headers: hdrs() });
      if (!r.ok) return [];
      return r.json();
    },
    enabled: bulkDialog && !!bulkScopeId,
  });

  const bulkAllocate = useMutation({
    mutationFn: async (body) => {
      const r = await fetch(`${API_URL}/api/housing/bulk-allocate`, { method: 'POST', headers: hdrs(), body: JSON.stringify(body) });
      if (!r.ok) { const err = await r.json().catch(() => ({})); throw new Error(err.detail || 'Bulk allocate failed'); }
      return r.json();
    },
    onSuccess: (r) => {
      toast.success(`Allocated ${r.allocated_count} · Skipped ${r.skipped_count}`);
      invalidateAll();
      setBulkDialog(false);
      setBulkSelection({});
    },
    onError: (e) => toast.error(e.message),
  });

  // Candidate rooms to link against (the counterpart scope, not already linked)
  const linkCandidates = useMemo(() => {
    if (!linkDialog) return [];
    const otherScope = linkDialog.scope === 'school' ? 'event' : 'school';
    return rooms.filter((r) => r.scope === otherScope && !r.link_id);
  }, [linkDialog, rooms]);

  const byScope = overview?.by_scope || [];

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="unified-housing-page">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
              <span className="p-2 rounded-xl bg-gradient-to-br from-indigo-500 to-sky-500 text-white">
                <Home className="h-6 w-6" />
              </span>
              Unified Housing
            </h1>
            <p className="text-slate-500 mt-1">
              Every hostel, guest room, and allocation across schools + events — in one view.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Input
              placeholder="Tenant ID (optional)"
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value.trim())}
              className="w-64"
              data-testid="housing-tenant-filter"
            />
            <Select value={scopeFilter} onValueChange={setScopeFilter}>
              <SelectTrigger className="w-40" data-testid="housing-scope-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All scopes</SelectItem>
                <SelectItem value="school">Schools only</SelectItem>
                <SelectItem value="event">Events only</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={() => setBuildingDialog(true)} data-testid="new-building-btn">
              <Plus className="h-4 w-4 mr-1" /> New building
            </Button>
            <Button variant="outline" onClick={() => setBulkDialog(true)} data-testid="bulk-allocate-btn">
              <Zap className="h-4 w-4 mr-1" /> Bulk allocate
            </Button>
            <Button variant="outline" size="icon" onClick={() => refetchOv()} data-testid="housing-refresh-btn">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {loadingOv ? (
            Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-28" />)
          ) : (
            <>
              <KpiCard title="Blocks" value={overview?.total_blocks ?? 0} icon={Building2} tone="indigo" />
              <KpiCard title="Rooms" value={overview?.total_rooms ?? 0} icon={BedDouble} tone="indigo" />
              <KpiCard
                title="Occupancy"
                value={`${overview?.occupancy_rate ?? 0}%`}
                icon={Users}
                tone={overview?.occupancy_rate > 80 ? 'rose' : 'emerald'}
                sub={`${overview?.total_occupied ?? 0} / ${overview?.total_capacity ?? 0} beds`}
              />
              <KpiCard title="Active Allocations" value={overview?.active_allocations ?? 0} icon={Home} tone="emerald" />
              <KpiCard title="Revenue 30d" value={fmtMoney(overview?.revenue_30d)} icon={TrendingUp} tone="amber" sub="from housing ledger" />
            </>
          )}
        </div>

        {/* Scope split */}
        {byScope.length > 0 && (
          <Card>
            <CardHeader className="pb-3"><CardTitle className="text-base">Occupancy by scope</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {byScope.map((s) => {
                const rate = s.capacity ? Math.round((s.occupied / s.capacity) * 100) : 0;
                return (
                  <div key={s.scope} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium flex items-center gap-2">
                        <ScopeBadge scope={s.scope} />
                        <span className="text-slate-600">· {s.blocks} blocks · {s.rooms} rooms</span>
                      </span>
                      <span className="text-slate-900 font-semibold">{s.occupied} / {s.capacity} <span className="text-slate-400 text-xs">({rate}%)</span></span>
                    </div>
                    <Progress value={rate} className="h-2" />
                  </div>
                );
              })}
            </CardContent>
          </Card>
        )}

        {/* Tabs */}
        <Tabs defaultValue="blocks" className="w-full">
          <TabsList className="grid grid-cols-3 sm:grid-cols-4 h-auto gap-1 md:inline-flex md:grid-cols-none">
            <TabsTrigger value="blocks" data-testid="housing-tab-blocks">Blocks</TabsTrigger>
            <TabsTrigger value="rooms" data-testid="housing-tab-rooms">Rooms</TabsTrigger>
            <TabsTrigger value="floorplan" data-testid="housing-tab-floorplan">Floor plan</TabsTrigger>
            <TabsTrigger value="allocations" data-testid="housing-tab-allocations">Allocations</TabsTrigger>
            {(isSuper || can('housing.manage') || roleLoading) && (
              <TabsTrigger value="links" data-testid="housing-tab-links">Cross-scope Links</TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="blocks" className="mt-4">
            <Card>
              <CardContent className="pt-4">
                {loadingB ? <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div> : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Scope</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Tenant</TableHead>
                        <TableHead className="text-right">Rooms</TableHead>
                        <TableHead className="text-right">Capacity</TableHead>
                        <TableHead className="text-right">Occupancy</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {blocks.length === 0 ? (
                        <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-8">No blocks. Click <span className="font-semibold">New building</span> to add one.</TableCell></TableRow>
                      ) : blocks.map((b) => (
                        <TableRow key={`${b.scope}-${b.id}`} data-testid={`housing-block-${b.id}`}>
                          <TableCell><ScopeBadge scope={b.scope} /></TableCell>
                          <TableCell className="font-medium">{b.name}</TableCell>
                          <TableCell className="text-xs font-mono text-slate-500">{b.scope_label}</TableCell>
                          <TableCell className="text-right">{b.room_count}</TableCell>
                          <TableCell className="text-right">{b.capacity}</TableCell>
                          <TableCell className="text-right">
                            <span className={`font-semibold ${b.occupancy_rate > 80 ? 'text-rose-600' : 'text-emerald-600'}`}>
                              {b.occupancy}/{b.capacity} ({b.occupancy_rate}%)
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button size="sm" variant="ghost" className="h-7 text-xs"
                              onClick={() => { setRoomDialog({ building: b }); setRForm({ room_number: '', capacity: 1, room_type: 'standard', floor: 1, price_per_night: 0 }); }}
                              data-testid={`add-room-to-${b.id}`}>
                              <Plus className="h-3 w-3 mr-1" /> Add room
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="rooms" className="mt-4">
            <Card>
              <CardContent className="pt-4">
                {loadingR ? <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div> : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Scope</TableHead>
                        <TableHead>Block / Building</TableHead>
                        <TableHead>Room</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead className="text-right">Capacity</TableHead>
                        <TableHead className="text-right">Occupants</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Link</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rooms.length === 0 ? (
                        <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-8">No rooms.</TableCell></TableRow>
                      ) : rooms.map((r) => (
                        <TableRow key={`${r.scope}-${r.id}`} data-testid={`housing-room-${r.id}`}>
                          <TableCell><ScopeBadge scope={r.scope} /></TableCell>
                          <TableCell className="text-sm">{r.parent_name || '—'}</TableCell>
                          <TableCell className="font-medium">{r.room_number}</TableCell>
                          <TableCell className="text-xs capitalize text-slate-600">{r.room_type}</TableCell>
                          <TableCell className="text-right">{r.capacity}</TableCell>
                          <TableCell>
                            <div className="flex flex-col items-end gap-1">
                              <span className="text-sm font-semibold">{r.occupants}</span>
                              {(r.occupant_names || []).length > 0 && (
                                <div className="flex flex-wrap gap-1 justify-end max-w-[220px]" data-testid={`room-occupants-${r.id}`}>
                                  {r.occupant_names.slice(0, 3).map((n, i) => (
                                    <Badge key={i} variant="outline" className="text-[10px] px-1.5 py-0 font-normal">
                                      <UserCheck className="h-2.5 w-2.5 mr-0.5" /> {n}
                                    </Badge>
                                  ))}
                                  {r.occupant_names.length > 3 && (
                                    <Badge variant="outline" className="text-[10px] px-1.5 py-0">+{r.occupant_names.length - 3}</Badge>
                                  )}
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant={r.status === 'occupied' ? 'default' : 'outline'} className="capitalize">{r.status}</Badge>
                          </TableCell>
                          <TableCell>
                            {r.link_id ? (
                              <Button
                                size="sm" variant="ghost"
                                className="h-7 text-xs text-emerald-600 hover:text-rose-600"
                                onClick={() => { if (window.confirm('Unlink these rooms? Their availability will no longer be shared.')) deleteLink.mutate(r.link_id); }}
                                data-testid={`unlink-room-${r.id}`}
                              >
                                <Link2 className="h-3 w-3 mr-1" /> Linked
                              </Button>
                            ) : (
                              <Button
                                size="sm" variant="ghost"
                                className="h-7 text-xs"
                                onClick={() => setLinkDialog({ scope: r.scope, roomId: r.id, roomNumber: r.room_number, parentName: r.parent_name })}
                                data-testid={`link-room-${r.id}`}
                              >
                                <Link2 className="h-3 w-3 mr-1" /> Link…
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Floor plan — grouped grid with status colors */}
          <TabsContent value="floorplan" className="mt-4" data-testid="floorplan-tab-content">
            {loadingR ? (
              <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div>
            ) : (
              <FloorPlanView rooms={rooms} onCheckout={(a) => { if (window.confirm(`Check out ${a.name}?`)) checkoutAlloc.mutate({ id: a.allocation_id, scope: a.scope }); }} />
            )}
          </TabsContent>

          <TabsContent value="allocations" className="mt-4">
            <Card>
              <CardContent className="pt-4">
                {loadingA ? <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div> : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Scope</TableHead>
                        <TableHead>Occupant</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Room</TableHead>
                        <TableHead>Check-in</TableHead>
                        <TableHead>Check-out</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Amount</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {allocs.length === 0 ? (
                        <TableRow><TableCell colSpan={9} className="text-center text-slate-500 py-8">No allocations.</TableCell></TableRow>
                      ) : allocs.map((a) => {
                        // 'active' means currently checked in - show checkout button
                        const checkedIn = (a.status === 'active' || a.status === 'checked_in');
                        // 'confirmable' means can be checked in - show checkin button
                        const confirmable = (a.scope === 'event' && (a.status === 'confirmed' || a.status === 'pending'));
                        // For badge styling, both checked-in and confirmed are "active" visually
                        const badgeActive = checkedIn || a.status === 'confirmed';
                        return (
                          <TableRow key={`${a.scope}-${a.id}`} data-testid={`housing-alloc-${a.id}`}>
                            <TableCell><ScopeBadge scope={a.scope} /></TableCell>
                            <TableCell className="font-medium">{a.occupant_name || '—'}</TableCell>
                            <TableCell className="text-xs capitalize text-slate-600">{a.occupant_type}</TableCell>
                            <TableCell>{a.block_name ? `${a.block_name} / ` : ''}{a.room_number}</TableCell>
                            <TableCell className="text-xs">{a.check_in ? new Date(a.check_in).toLocaleDateString() : '—'}</TableCell>
                            <TableCell className="text-xs">{a.check_out ? new Date(a.check_out).toLocaleDateString() : '—'}</TableCell>
                            <TableCell>
                              <Badge variant={badgeActive ? 'default' : 'outline'} className="capitalize text-[10px]">{a.status || '—'}</Badge>
                            </TableCell>
                            <TableCell className="text-right">{a.amount ? fmtMoney(a.amount) : '—'}</TableCell>
                            <TableCell className="text-right">
                              {checkedIn ? (
                                <Button size="sm" variant="ghost" className="h-7 text-xs text-rose-600 hover:text-rose-700"
                                  onClick={() => { if (window.confirm(`Check out ${a.occupant_name}?`)) checkoutAlloc.mutate({ id: a.id, scope: a.scope }); }}
                                  data-testid={`checkout-alloc-${a.id}`}>
                                  <LogOut className="h-3 w-3 mr-1" /> Check out
                                </Button>
                              ) : confirmable ? (
                                <Button size="sm" variant="ghost" className="h-7 text-xs text-emerald-600 hover:text-emerald-700"
                                  onClick={() => checkinAlloc.mutate({ id: a.id, scope: a.scope })}
                                  data-testid={`checkin-alloc-${a.id}`}>
                                  <LogIn className="h-3 w-3 mr-1" /> Check in
                                </Button>
                              ) : null}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Cross-scope links */}
          <TabsContent value="links" className="mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Link2 className="h-5 w-5 text-indigo-500" /> Cross-scope Room Links</CardTitle>
                <p className="text-xs text-slate-500 mt-1">
                  When a physical room exists in both the school hostel and the event guest-house inventory, link them here so an occupied
                  student room automatically blocks event bookings for the same dates — and vice versa.
                </p>
              </CardHeader>
              <CardContent>
                {loadingL ? <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div> : links.length === 0 ? (
                  <p className="text-sm text-slate-500 py-6 text-center">No rooms linked yet. Use the <span className="font-medium">Link…</span> action on a row in the Rooms tab.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>School room</TableHead>
                        <TableHead>Event room</TableHead>
                        <TableHead>Note</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {links.map((lk) => (
                        <TableRow key={lk.id} data-testid={`link-row-${lk.id}`}>
                          <TableCell className="text-sm">{lk.school_block_name && `${lk.school_block_name} / `}<span className="font-medium">{lk.school_room_number}</span></TableCell>
                          <TableCell className="text-sm">{lk.event_building_name && `${lk.event_building_name} / `}<span className="font-medium">{lk.event_room_number}</span></TableCell>
                          <TableCell className="text-xs text-slate-500">{lk.note || '—'}</TableCell>
                          <TableCell className="text-xs text-slate-500">{lk.created_at ? new Date(lk.created_at).toLocaleDateString() : ''}</TableCell>
                          <TableCell className="text-right">
                            <Button size="sm" variant="ghost" className="h-7 text-xs text-rose-600"
                              onClick={() => { if (window.confirm('Unlink these rooms?')) deleteLink.mutate(lk.id); }}
                              data-testid={`unlink-link-${lk.id}`}>
                              <Unlink className="h-3 w-3 mr-1" /> Unlink
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Link dialog */}
        <Dialog open={!!linkDialog} onOpenChange={(o) => { if (!o) { setLinkDialog(null); setLinkTarget(''); setLinkNote(''); } }}>
          <DialogContent data-testid="link-room-dialog">
            <DialogHeader>
              <DialogTitle>Link this room to the {linkDialog?.scope === 'school' ? 'event' : 'school'} inventory</DialogTitle>
              <DialogDescription>
                Pairing room <span className="font-semibold">{linkDialog?.parentName} / {linkDialog?.roomNumber}</span> ({linkDialog?.scope}). Once linked, an active allocation on either side will block the other for overlapping dates.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label className="text-xs">Counterpart room</Label>
                <Select value={linkTarget} onValueChange={setLinkTarget}>
                  <SelectTrigger data-testid="link-counterpart-select">
                    <SelectValue placeholder={`Choose a ${linkDialog?.scope === 'school' ? 'event' : 'school'} room`} />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {linkCandidates.length === 0
                      ? <div className="px-2 py-4 text-xs text-slate-500 text-center">No unlinked rooms on the counterpart side.</div>
                      : linkCandidates.map((c) => (
                          <SelectItem key={c.id} value={c.id}>
                            {c.parent_name ? `${c.parent_name} / ` : ''}{c.room_number} <span className="text-xs text-slate-400 ml-1">({c.room_type})</span>
                          </SelectItem>
                        ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Note (optional)</Label>
                <Input value={linkNote} onChange={(e) => setLinkNote(e.target.value)} placeholder="e.g. Guest wing rooms 301-305" data-testid="link-note-input" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setLinkDialog(null)} data-testid="link-cancel-btn">Cancel</Button>
              <Button
                disabled={!linkTarget || createLink.isPending}
                onClick={() => {
                  const schoolRoomId = linkDialog.scope === 'school' ? linkDialog.roomId : linkTarget;
                  const eventRoomId = linkDialog.scope === 'event' ? linkDialog.roomId : linkTarget;
                  createLink.mutate({ schoolRoomId, eventRoomId, note: linkNote });
                }}
                data-testid="link-confirm-btn"
              >
                {createLink.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Link rooms'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* New Building dialog */}
        <Dialog open={buildingDialog} onOpenChange={(o) => !o && setBuildingDialog(false)}>
          <DialogContent data-testid="new-building-dialog">
            <DialogHeader>
              <DialogTitle>New building / block</DialogTitle>
              <DialogDescription>Create housing inventory in one place — choose the scope and we route it to the right collection.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label className="text-xs">Scope</Label>
                <Select value={bForm.scope} onValueChange={(v) => setBForm({ ...bForm, scope: v })}>
                  <SelectTrigger data-testid="building-scope-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="event">Event / standalone guest-house</SelectItem>
                    <SelectItem value="school">School hostel block</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {bForm.scope === 'school' && (
                <>
                  <div>
                    <Label className="text-xs">School (tenant)</Label>
                    <Select value={bForm.tenant_id} onValueChange={(v) => setBForm({ ...bForm, tenant_id: v })}>
                      <SelectTrigger data-testid="building-tenant-select"><SelectValue placeholder="Choose a school…" /></SelectTrigger>
                      <SelectContent>
                        {(tenants || []).map((t) => (<SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Gender</Label>
                    <Select value={bForm.gender} onValueChange={(v) => setBForm({ ...bForm, gender: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="mixed">Mixed</SelectItem>
                        <SelectItem value="male">Boys</SelectItem>
                        <SelectItem value="female">Girls</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
              <div>
                <Label className="text-xs">Name</Label>
                <Input value={bForm.name} onChange={(e) => setBForm({ ...bForm, name: e.target.value })} placeholder={bForm.scope === 'school' ? 'e.g. Girls Hostel A' : 'e.g. Riverside Guest House'} data-testid="building-name-input" />
              </div>
              <div>
                <Label className="text-xs">Description</Label>
                <Input value={bForm.description} onChange={(e) => setBForm({ ...bForm, description: e.target.value })} />
              </div>
              {bForm.scope === 'event' && (
                <>
                  <div>
                    <Label className="text-xs">Address</Label>
                    <Input value={bForm.address} onChange={(e) => setBForm({ ...bForm, address: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-xs">Total floors</Label>
                    <Input type="number" min="1" value={bForm.total_floors} onChange={(e) => setBForm({ ...bForm, total_floors: parseInt(e.target.value) || 1 })} />
                  </div>
                </>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBuildingDialog(false)}>Cancel</Button>
              <Button
                disabled={!bForm.name.trim() || (bForm.scope === 'school' && !bForm.tenant_id) || createBuilding.isPending}
                onClick={() => createBuilding.mutate(bForm)}
                data-testid="building-create-btn"
              >
                {createBuilding.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create building'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* New Room dialog */}
        <Dialog open={!!roomDialog} onOpenChange={(o) => !o && setRoomDialog(null)}>
          <DialogContent data-testid="new-room-dialog">
            <DialogHeader>
              <DialogTitle>Add room to {roomDialog?.building?.name}</DialogTitle>
              <DialogDescription>Scope: <span className="capitalize font-medium">{roomDialog?.building?.scope}</span></DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Room number</Label>
                  <Input value={rForm.room_number} onChange={(e) => setRForm({ ...rForm, room_number: e.target.value })} placeholder="e.g. 101" data-testid="room-number-input" />
                </div>
                <div>
                  <Label className="text-xs">Capacity</Label>
                  <Input type="number" min="1" value={rForm.capacity} onChange={(e) => setRForm({ ...rForm, capacity: parseInt(e.target.value) || 1 })} data-testid="room-capacity-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Room type</Label>
                  <Select value={rForm.room_type} onValueChange={(v) => setRForm({ ...rForm, room_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="standard">Standard</SelectItem>
                      <SelectItem value="deluxe">Deluxe</SelectItem>
                      <SelectItem value="suite">Suite</SelectItem>
                      <SelectItem value="dorm">Dormitory</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Floor</Label>
                  <Input type="number" min="1" value={rForm.floor} onChange={(e) => setRForm({ ...rForm, floor: parseInt(e.target.value) || 1 })} />
                </div>
              </div>
              {roomDialog?.building?.scope === 'event' && (
                <div>
                  <Label className="text-xs">Price per night (USD)</Label>
                  <Input type="number" min="0" step="0.01" value={rForm.price_per_night} onChange={(e) => setRForm({ ...rForm, price_per_night: parseFloat(e.target.value) || 0 })} data-testid="room-price-input" />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRoomDialog(null)}>Cancel</Button>
              <Button
                disabled={!rForm.room_number.trim() || createRoom.isPending}
                onClick={() => createRoom.mutate({
                  scope: roomDialog.building.scope,
                  building_id: roomDialog.building.id,
                  tenant_id: roomDialog.building.tenant_id || undefined,
                  room_number: rForm.room_number,
                  capacity: rForm.capacity,
                  room_type: rForm.room_type,
                  floor: rForm.floor,
                  price_per_night: rForm.price_per_night,
                })}
                data-testid="room-create-btn"
              >
                {createRoom.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create room'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Allocate dialog */}
        <Dialog open={bulkDialog} onOpenChange={(o) => { if (!o) { setBulkDialog(false); setBulkScopeId(''); setBulkSelection({}); } }}>
          <DialogContent className="max-w-2xl" data-testid="bulk-allocate-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Zap className="h-4 w-4 text-amber-500" /> Bulk allocate event rooms</DialogTitle>
              <DialogDescription>Auto-assign the next available event-scope room to each selected registration. Already-booked registrations and missing rooms are reported back.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label className="text-xs">Event / campaign scope id</Label>
                <Input value={bulkScopeId} onChange={(e) => { setBulkScopeId(e.target.value); setBulkSelection({}); }} placeholder="Paste the event's scope_id…" data-testid="bulk-scope-input" />
              </div>
              <div>
                <Label className="text-xs">Restrict to building (optional)</Label>
                <Select value={bulkBuildingId || 'any'} onValueChange={(v) => setBulkBuildingId(v === 'any' ? '' : v)}>
                  <SelectTrigger data-testid="bulk-building-select"><SelectValue placeholder="Any building" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any building</SelectItem>
                    {(blocks || []).filter((b) => b.scope === 'event').map((b) => (
                      <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {bulkScopeId && (
                <div className="border rounded-md max-h-64 overflow-y-auto" data-testid="bulk-reg-list">
                  {bulkRegs.length === 0 ? (
                    <div className="p-4 text-xs text-slate-500 text-center">No registrations found for this scope id.</div>
                  ) : bulkRegs.map((reg) => (
                    <label key={reg.id} className="flex items-center gap-2 px-3 py-2 border-b last:border-0 hover:bg-slate-50 cursor-pointer">
                      <input type="checkbox" checked={!!bulkSelection[reg.id]} onChange={(e) => setBulkSelection((s) => ({ ...s, [reg.id]: e.target.checked }))} data-testid={`bulk-reg-check-${reg.id}`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate">{reg.full_name || '(unnamed)'}</div>
                        <div className="text-xs text-slate-500 truncate">{reg.email || '—'} · <span className="capitalize">{reg.status}</span></div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
              <div className="flex items-center justify-between text-xs text-slate-500">
                <span>{Object.values(bulkSelection).filter(Boolean).length} selected</span>
                <div className="flex gap-2">
                  <Button type="button" size="sm" variant="ghost" className="h-6 text-[11px]" onClick={() => { const m = {}; bulkRegs.forEach(r => m[r.id] = true); setBulkSelection(m); }}>Select all</Button>
                  <Button type="button" size="sm" variant="ghost" className="h-6 text-[11px]" onClick={() => setBulkSelection({})}>Clear</Button>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBulkDialog(false)}>Cancel</Button>
              <Button
                disabled={bulkAllocate.isPending || Object.values(bulkSelection).filter(Boolean).length === 0}
                onClick={() => {
                  const ids = Object.entries(bulkSelection).filter(([_, v]) => v).map(([k]) => k);
                  bulkAllocate.mutate({ registration_ids: ids, building_id: bulkBuildingId || undefined });
                }}
                data-testid="bulk-allocate-confirm-btn"
              >
                {bulkAllocate.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Allocate rooms'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}

// ===== Floor Plan View =====
const FloorPlanView = ({ rooms, onCheckout }) => {
  // Group by scope + building/block
  const grouped = React.useMemo(() => {
    const g = {};
    for (const r of rooms) {
      const key = `${r.scope}::${r.parent_id || 'unassigned'}::${r.parent_name || 'Unassigned'}`;
      if (!g[key]) g[key] = { scope: r.scope, name: r.parent_name || 'Unassigned', rooms: [] };
      g[key].rooms.push(r);
    }
    return Object.values(g).sort((a, b) => (a.scope + a.name).localeCompare(b.scope + b.name));
  }, [rooms]);

  const statusColor = (s) => ({
    available: 'bg-emerald-50 border-emerald-300 text-emerald-800',
    partial: 'bg-amber-50 border-amber-300 text-amber-800',
    occupied: 'bg-rose-50 border-rose-300 text-rose-800',
    dirty: 'bg-slate-100 border-slate-400 text-slate-700',
    cleaning: 'bg-sky-50 border-sky-300 text-sky-700',
    reserved: 'bg-violet-50 border-violet-300 text-violet-800',
  }[s] || 'bg-slate-50 border-slate-300 text-slate-700');

  if (grouped.length === 0) {
    return (
      <Card><CardContent className="py-16 text-center text-slate-500">No rooms to plot yet. Create a building and add rooms.</CardContent></Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="floor-plan-view">
      <div className="flex flex-wrap gap-3 text-xs">
        <LegendChip label="Available" className="bg-emerald-50 border-emerald-300 text-emerald-800" />
        <LegendChip label="Partial" className="bg-amber-50 border-amber-300 text-amber-800" />
        <LegendChip label="Occupied" className="bg-rose-50 border-rose-300 text-rose-800" />
        <LegendChip label="Dirty" className="bg-slate-100 border-slate-400 text-slate-700" />
        <LegendChip label="Cleaning" className="bg-sky-50 border-sky-300 text-sky-700" />
      </div>
      {grouped.map((g, gi) => (
        <Card key={gi} data-testid={`floorplan-group-${gi}`}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <MapPin className="h-3.5 w-3.5 text-indigo-500" />
              <ScopeBadge scope={g.scope} />
              <span>{g.name}</span>
              <span className="text-xs text-slate-500 font-normal">· {g.rooms.length} rooms</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-7 lg:grid-cols-10 gap-2">
              {g.rooms.sort((a, b) => (a.room_number || '').localeCompare(b.room_number || '')).map((r) => (
                <FloorRoomCell key={r.id} room={r} className={statusColor(r.status)} onCheckout={onCheckout} />
              ))}
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

const LegendChip = ({ label, className }) => (
  <span className={`inline-flex items-center gap-1 rounded border px-2 py-0.5 ${className}`}>
    <span className="inline-block w-2 h-2 rounded-full bg-current opacity-60" /> {label}
  </span>
);

const FloorRoomCell = ({ room, className, onCheckout }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`w-full rounded border p-2 text-left transition-all hover:shadow-md focus:outline-none focus:ring-2 focus:ring-indigo-500/40 ${className}`}
        data-testid={`floor-cell-${room.id}`}
      >
        <div className="text-xs font-bold leading-none">{room.room_number || '—'}</div>
        <div className="text-[10px] opacity-80 mt-1">{room.occupants}/{room.capacity}</div>
      </button>
      {open && (
        <div className="absolute z-20 mt-1 w-56 rounded-lg border bg-white shadow-xl p-3 text-xs" onMouseLeave={() => setOpen(false)} data-testid={`floor-popover-${room.id}`}>
          <div className="font-semibold text-slate-900">Room {room.room_number}</div>
          <div className="text-slate-500 mb-2">{room.room_type} · cap {room.capacity} · {room.status}</div>
          {(room.occupant_details || []).length === 0 ? (
            <div className="text-slate-500 italic">No occupants</div>
          ) : (
            <ul className="space-y-1">
              {(room.occupant_details || []).map((o, i) => (
                <li key={i} className="flex items-center justify-between gap-2">
                  <span className="truncate">{o.name || '(unnamed)'}</span>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setOpen(false); onCheckout({ ...o, scope: room.scope }); }}
                    className="text-rose-600 hover:text-rose-700 text-[10px] font-medium"
                    data-testid={`floor-checkout-${o.allocation_id}`}
                  >
                    Check out
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};
